import { createContext, useCallback, useContext } from "react";
import { useSearchParams } from "react-router-dom";

const USER_PARAM = "user";

interface ViewContextValue {
  /** UUID of the selected owner filter, or null for global (all owners). */
  ownerUserId: string | null;
  /** Update the owner filter. Pass null to clear (global view). */
  setOwnerUserId: (id: string | null) => void;
}

const ViewContext = createContext<ViewContextValue>({
  ownerUserId: null,
  setOwnerUserId: () => {},
});

export function ViewProvider({ children }: { children: React.ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerUserId = searchParams.get(USER_PARAM);

  const setOwnerUserId = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) {
            next.set(USER_PARAM, id);
          } else {
            next.delete(USER_PARAM);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <ViewContext.Provider value={{ ownerUserId, setOwnerUserId }}>{children}</ViewContext.Provider>
  );
}

export function useView() {
  return useContext(ViewContext);
}
